import React, { useEffect, useState } from 'react';
import { x } from '@xstyled/styled-components';
import { Chapter, Sequence, TrainingState, useTrainingStore } from '../state';
import { NavItem } from './NavItem';
import { select } from '~/lib/select';

const selector = (s: TrainingState) =>
  select(s, 'items', 'activeItemPosition', 'status');

type ChapterWithChildren = Chapter & { children: Sequence[] };
type OrganizedItem = ChapterWithChildren | Sequence;

const chapterPanelId = (chapterId: string) => `nav-chapter-panel-${chapterId}`;

const ListRenderer = ({
  level,
  items,
  openChapter,
  activeItemPos,
  setOpenChapter,
  listId,
  hidden,
  testedOut = false
}: {
  level: number;
  items: OrganizedItem[];
  openChapter: string;
  activeItemPos: number;
  setOpenChapter: (id: string) => void;
  /** Optional id for the list (chapter panel target for aria-controls). */
  listId?: string;
  /** Keep collapsed panels in the DOM for aria-controls (CAT-1293). */
  hidden?: boolean;
  /** The slides of a chapter the learner tested out of in the pretest. */
  testedOut?: boolean;
}) => {
  const getContent = () => {
    const content = items.map((item: OrganizedItem) => {
      const isChapter = item.kind === 'chapter';
      const panelId = isChapter ? chapterPanelId(item.id) : undefined;
      const isExpanded = item.id === openChapter;
      const children = (item as ChapterWithChildren).children;
      const itemTestedOut = isChapter ? !!item.testedOut : testedOut;

      return (
        <li key={item.id}>
          <NavItem
            item={item}
            expanded={isExpanded}
            activeItemPosition={activeItemPos}
            panelId={panelId}
            testedOut={itemTestedOut}
            onClick={() => {
              if (isChapter) {
                setOpenChapter(item.id);
              }
            }}
          />
          {isChapter && (
            <ListRenderer
              level={1}
              listId={panelId}
              hidden={!isExpanded}
              items={children ?? []}
              openChapter={openChapter}
              activeItemPos={activeItemPos}
              setOpenChapter={setOpenChapter}
              testedOut={itemTestedOut}
            />
          )}
        </li>
      );
    });
    return content;
  };

  return level == 0 ? (
    <x.ol p="sm" m={0}>
      {getContent()}
    </x.ol>
  ) : (
    <x.ul id={listId} hidden={hidden || undefined} p="sm" m={0}>
      {getContent()}
    </x.ul>
  );
};

export const TableOfContent = () => {
  const { items, activeItemPosition, status } = useTrainingStore(selector);
  const [activeItemPos, setActiveItemPos] = useState(activeItemPosition || 0);

  if (status === 'completed' && activeItemPos < 0 && items) {
    setActiveItemPos(items[items.length - 1]['position']);
  }

  const [openChapter, setOpenChapter] = useState('');

  useEffect(() => {
    if (!items || activeItemPos < 0) return;
    const item = items.find(i => i.position === activeItemPos) as Sequence;
    setOpenChapter(item.chapterId);
  }, [items, activeItemPos]);

  const organizedList = items.reduce((acc: ChapterWithChildren[], item) => {
    if (!(item as Sequence).chapterId) {
      // It's a chapter
      acc.push({ ...item, children: [] } as ChapterWithChildren);
    } else {
      // It's a sequence, find its chapter and add it as a child
      const chapter = acc.find(
        chap => chap.id === (item as Sequence).chapterId
      );
      if (chapter) {
        chapter?.children?.push(item as Sequence);
      }
    }
    return acc;
  }, []);

  return (
    <ListRenderer
      level={0}
      items={organizedList}
      activeItemPos={activeItemPos}
      openChapter={openChapter}
      setOpenChapter={setOpenChapter}
    />
  );
};
